import { apiClient, apiResources, type ApiResourceMeta } from '@kss/api';

/**
 * Foreign-key resolver for registry-driven pages.
 *
 * The user complaint that triggered this file: "in review page all
 * dropdown value showing it should be name such as department, company,
 * grade". Numeric `departmentId` / `designationId` / `companyId`
 * columns were rendering as raw integers in both list grids and
 * detail/edit forms.
 *
 * Strategy:
 *   1. Detect FK fields by naming convention — a numeric field whose
 *      name ends in `Id` (case-sensitive on `Id`) and which is NOT the
 *      resource's own id-field.
 *   2. Strip the `Id` suffix and look the remainder up in the registry
 *      (case-insensitive on the entity name). Some common aliases are
 *      handled too — `reportingManagerId` → Employee,
 *      `parentId` → the row's own resource.
 *   3. Fetch the target resource's list once per session, cache it in
 *      a module-level Map keyed by basePath, and reuse for every cell.
 *   4. Resolve a single value with `resolveFkLabel(field, value)` →
 *      `"Sales · SAL-01"` (name preferred, code fallback, then id).
 *   5. Provide the full option set with `getFkOptions(field)` so form
 *      fields can render a `<select>` instead of a number input.
 *
 * Backend assumption: target endpoints return either a bare array or
 * an `{ items: [...] }` / `{ data: [...] }` envelope. Anything else is
 * treated as "no rows" and the FK falls back to its raw value.
 */

export interface FkOption {
  id: string | number;
  label: string;
  /** Secondary text (e.g. customer + amount on an invoice option). */
  description?: string;
}

interface ResolverContext {
  /** The resource the cell or field belongs to. Used for self-FK like `parentId`. */
  selfResource?: ApiResourceMeta;
}

const cache = new Map<string, FkOption[]>();
const pending = new Map<string, Promise<FkOption[]>>();

/**
 * Aliases for FK column names whose stem doesn't equal an entity name.
 *
 * Why these are needed: the convention is "stripIdSuffix → match
 * resource by name". That works for `customerId` → `Customer` but
 * breaks down when:
 *   (a) the FK column has a compound prefix (`managerEmployeeId`,
 *       `approvedByEmployeeId`, `requestedByEmployeeId`, …) — the
 *       prefix isn't an entity name
 *   (b) the FE convention dropped a prefix the backend kept
 *       (`brandId` should resolve to ProductBrand, `categoryId` to
 *       ProductCategory)
 *
 * Key is lowercased; value is the exact `apiResources.name` to use.
 */
const ALIASES: Record<string, string> = {
  reportingmanagerid: 'Employee',
  parentmenuid: 'Menu',
  parentaccountid: 'Account',
  parentcategoryid: 'ProductCategory',
  createdby: 'User',
  modifiedby: 'User',
  approverid: 'User',
  managerid: 'Employee',
  customerid: 'Customer',
  supplierid: 'Supplier',
  vendorid: 'Supplier',
  ownerid: 'User',
  assignedtoid: 'User',
  issuedtouserid: 'User',
  // — added 2026-05-29 after reality-check probe found the live
  //   `Brand` / `Category` names don't exist on the swagger; the
  //   registry exposes them under their qualified names.
  brandid: 'ProductBrand',
  categoryid: 'ProductCategory',
  // — Employee-flavoured FK columns whose prefixes aren't entities.
  manageremployeeid: 'Employee',
  approvedbyemployeeid: 'Employee',
  approvedby: 'Employee',
  requestedbyemployeeid: 'Employee',
  issuedbyemployeeid: 'Employee',
  salespersonid: 'Employee',
  heademployeeid: 'Employee',
  cashieremployeeid: 'Employee',     // PosShift.cashierEmployeeId
  servedbyemployeeid: 'Employee',
  preparedbyemployeeid: 'Employee',
  assignedemployeeid: 'Employee',
  reviewedbyemployeeid: 'Employee',
  reportingmanageremployeeid: 'Employee',
  reviereremployeeid: 'Employee',
  reviewerid: 'Employee',
  waiterid: 'Employee',
  deliveredbyemployeeid: 'Employee',
  // — Routing-flavoured FK columns
  posshiftid: 'PosShift',
  // — Restaurant FK columns with non-obvious stems
  tableid: 'RestaurantTable',           // Kot.tableId / RestaurantOrder.tableId
  menucategoryid: 'MenuCategory',        // MenuItem.menuCategoryId, etc.
  menuitemid: 'MenuItem',                // RestaurantOrderLine.menuItemId
  // — POS
  posterminalid: 'PosTerminal',
  cashdrawersessionid: 'CashDrawerSession',
  // — Accounting / banking
  bankaccountid: 'BankAccount',
  chartofaccountid: 'ChartOfAccount',
  fiscalyearid: 'FiscalYear',
  journalentryid: 'JournalEntry',
  // — Tenancy
  branchid: 'Branch',
  companyid: 'Company',
  // — Inventory / units. The unit master is `UnitOfMeasure` (there is no
  // `Unit` resource), so all unit FKs resolve to it. UnitConversion uses
  // fromUnitId / toUnitId — without these the dropdowns were empty.
  warehouseid: 'Warehouse',
  unitofmeasureid: 'UnitOfMeasure',
  unitid: 'UnitOfMeasure',
  fromunitid: 'UnitOfMeasure',
  tounitid: 'UnitOfMeasure',
  fromunitofmeasureid: 'UnitOfMeasure',
  tounitofmeasureid: 'UnitOfMeasure',
  // — Product structure
  productbundleid: 'ProductBundle',
  productvariantid: 'ProductVariant',
  // — CRM. The pipeline-stage master is `OpportunityStage` (there is no
  //   `Stage` resource), and a Quotation links to its source order via
  //   `convertedSalesOrderId`, not a plain `salesOrderId`.
  stageid: 'OpportunityStage',
  opportunitystageid: 'OpportunityStage',
  leadid: 'Lead',
  convertedsalesorderid: 'SalesOrder',
  opportunityid: 'Opportunity',
  // — Other compound FKs we've seen on the live swagger
  relatedinvoiceid: 'SalesInvoice',
  relatedorderid: 'SalesOrder',
  goodsreceiptnoteid: 'GoodsReceiptNote',
  purchaseorderid: 'PurchaseOrder',
  salesinvoiceid: 'SalesInvoice',
  purchaseinvoiceid: 'PurchaseInvoice',
  awardedsupplierid: 'Supplier',        // Rfq.awardedSupplierId
  salesorderid: 'SalesOrder',           // G92: SalesPayment.salesOrderId (resolves once backend deploys)
  // — Promotion / marketing (Coupon.promotionId, Promotion.applicableProductCategoryId)
  promotionid: 'Promotion',
  applicableproductcategoryid: 'ProductCategory',
  // — Stock transfer: from/to warehouse columns render as raw numbers without these
  fromwarehouseid: 'Warehouse',
  towarehouseid: 'Warehouse',
  sourcewarehouseid: 'Warehouse',
  destinationwarehouseid: 'Warehouse',
};

/**
 * Per-resource option formatters — richer dropdown labels than the generic
 * name/code. e.g. an invoice FK should read "<number> · <party> · ₹<total>"
 * so the user can tell invoices apart (the bare number/id wasn't enough).
 */
const RESOURCE_LABELERS: Record<string, (row: Record<string, unknown>) => { label: string; description?: string }> = {
  SalesInvoice: (r) => ({
    label: String(r.invoiceNumber ?? r.code ?? `#${r.id}`),
    description: [r.customerName, r.grandTotal != null ? `₹${Number(r.grandTotal).toLocaleString('en-IN')}` : '']
      .filter(Boolean).map(String).join(' · ') || undefined,
  }),
  PurchaseInvoice: (r) => ({
    label: String(r.invoiceNumber ?? r.code ?? `#${r.id}`),
    description: [r.supplierName, r.grandTotal != null ? `₹${Number(r.grandTotal).toLocaleString('en-IN')}` : '']
      .filter(Boolean).map(String).join(' · ') || undefined,
  }),
  SalesOrder: (r) => ({
    label: String(r.orderNumber ?? r.code ?? `#${r.id}`),
    description: [r.customerName, r.grandTotal != null ? `₹${Number(r.grandTotal).toLocaleString('en-IN')}` : '']
      .filter(Boolean).map(String).join(' · ') || undefined,
  }),
  PurchaseOrder: (r) => ({
    label: String(r.orderNumber ?? r.code ?? `#${r.id}`),
    description: [r.supplierName, r.grandTotal != null ? `₹${Number(r.grandTotal).toLocaleString('en-IN')}` : '']
      .filter(Boolean).map(String).join(' · ') || undefined,
  }),
};

/**
 * Find the registry entry that backs a foreign-key field name. Returns
 * undefined for non-FK fields (so callers can fall through to plain
 * rendering).
 */
export function lookupFkTarget(
  fieldName: string,
  ctx: ResolverContext = {},
): ApiResourceMeta | undefined {
  if (!fieldName) return undefined;
  const lc = fieldName.toLowerCase();

  // Self-referential FKs ("parent")
  if (ctx.selfResource && (lc === 'parentid' || lc === 'parent')) {
    return ctx.selfResource;
  }

  // Explicit aliases
  if (ALIASES[lc]) {
    return apiResources.find((r) => r.name === ALIASES[lc]);
  }

  // Convention: ends with "Id", strip it, match case-insensitive
  if (!fieldName.endsWith('Id')) return undefined;
  const stem = fieldName.slice(0, -2);
  if (!stem) return undefined;

  // Ignore the resource's own id field
  if (ctx.selfResource && fieldName === ctx.selfResource.idField) return undefined;

  const lcStem = stem.toLowerCase();
  const byStem = apiResources.find((r) => r.name.toLowerCase() === lcStem);
  if (byStem) return byStem;

  // Compound-FK fallback: a prefixed column like `awardedSupplierId` or
  // `billToCustomerId` still references the base entity named by its suffix.
  const SUFFIX: Array<[string, string]> = [
    ['supplierid', 'Supplier'], ['customerid', 'Customer'], ['employeeid', 'Employee'],
    ['userid', 'User'], ['productid', 'Product'],
  ];
  for (const [suf, name] of SUFFIX) {
    if (lc.endsWith(suf)) {
      const r = apiResources.find((res) => res.name === name);
      if (r) return r;
    }
  }
  return undefined;
}

/**
 * Load the target resource's rows and turn them into `{ id, label }`.
 * Results are memoised per `basePath` for the lifetime of the page —
 * the page is short-lived enough that a hard reload is the right
 * invalidation story.
 */
export async function getFkOptions(
  fieldName: string,
  ctx: ResolverContext = {},
): Promise<FkOption[]> {
  const target = lookupFkTarget(fieldName, ctx);
  if (!target) return [];
  const key = target.basePath;
  if (cache.has(key)) return cache.get(key)!;
  if (pending.has(key)) return pending.get(key)!;

  const p = (async () => {
    try {
      const r = await apiClient.get(target.basePath);
      const list = pickList(r.data);
      const idField = target.idField;
      const labeler = RESOURCE_LABELERS[target.name];
      const options: FkOption[] = list.map((row) => {
        // Foreign-key columns are stored as the parent's numeric PK
        // (e.g. Product.categoryId int → Category.id int), NOT the
        // business-code string. §35.3 made `code` the *display* PK
        // but the relational FK is still on the numeric id column.
        // Prefer row.id; fall back to row[idField] for entities like
        // Menu whose PK is a uniqueidentifier and `id` may be absent.
        const id = row.id ?? row[idField];
        const fmt = labeler ? labeler(row) : { label: pickLabel(row) };
        return { id: id as string | number, label: fmt.label, description: fmt.description };
      });
      cache.set(key, options);
      return options;
    } catch {
      cache.set(key, []);
      return [];
    } finally {
      pending.delete(key);
    }
  })();
  pending.set(key, p);
  return p;
}

/**
 * Synchronous lookup — works only after the options have been loaded
 * once. For lazy callers (table cells), pair with `prefetchFks`.
 */
export function resolveFkLabel(
  fieldName: string,
  value: unknown,
  ctx: ResolverContext = {},
): string | null {
  if (value == null || value === '') return null;
  const target = lookupFkTarget(fieldName, ctx);
  if (!target) return null;
  const opts = cache.get(target.basePath);
  if (!opts) return null;
  const match = opts.find((o) => String(o.id) === String(value));
  return match ? match.label : null;
}

/**
 * Pre-fetch every FK target referenced by the given rows. Use from a
 * useEffect so the table can render names on the second paint.
 */
export async function prefetchFks(
  rows: Record<string, unknown>[],
  ctx: ResolverContext = {},
): Promise<void> {
  if (!rows.length) return;
  const fields = new Set<string>();
  for (const row of rows.slice(0, 25)) {
    for (const k of Object.keys(row)) {
      if (lookupFkTarget(k, ctx)) fields.add(k);
    }
  }
  await Promise.all([...fields].map((f) => getFkOptions(f, ctx)));
}

/** Clear the resolver cache — call after a CRUD write so the next read is fresh. */
export function invalidateFkCache(basePath?: string): void {
  if (basePath) cache.delete(basePath);
  else cache.clear();
}

/* -------------------------------------------------------------------- */
/*  Helpers                                                             */
/* -------------------------------------------------------------------- */

function pickList(d: unknown): Record<string, unknown>[] {
  if (Array.isArray(d)) return d as Record<string, unknown>[];
  if (d && typeof d === 'object') {
    const obj = d as { items?: unknown[]; data?: unknown[] | { items?: unknown[] } };
    if (Array.isArray(obj.items)) return obj.items as Record<string, unknown>[];
    if (Array.isArray(obj.data)) return obj.data as Record<string, unknown>[];
    if (obj.data && Array.isArray((obj.data as { items?: unknown[] }).items)) {
      return (obj.data as { items: unknown[] }).items as Record<string, unknown>[];
    }
  }
  return [];
}

/**
 * Pick a human label from a row. Priority order chosen to match what
 * end users actually want to see in dropdowns:
 *
 *   1. `displayName` (some entities like ExternalIdentityProvider)
 *   2. `name` (the universal choice for master-data)
 *   3. `firstName + lastName` (Employee, Customer, User)
 *   4. `fullName` (occasional alias)
 *   5. `username` (User-shaped rows)
 *   6. `code` (last-resort identifier)
 *   7. raw id
 */
function pickLabel(row: Record<string, unknown>): string {
  const display = row.displayName;
  if (typeof display === 'string' && display) return display;
  const name = row.name;
  if (typeof name === 'string' && name) return name;
  const first = row.firstName;
  const last = row.lastName;
  if (typeof first === 'string' || typeof last === 'string') {
    const combined = [first, last].filter(Boolean).join(' ').trim();
    if (combined) return combined;
  }
  const full = row.fullName;
  if (typeof full === 'string' && full) return full;
  const username = row.username;
  if (typeof username === 'string' && username) return username;
  const code = row.code;
  if (typeof code === 'string' && code) return code;
  const id = row.id ?? row.userId ?? row.employeeId;
  return id != null ? `#${id}` : '—';
}
